import { z } from "zod";
import { ChatEvent, ChatEventsCoverage, ServerToClient } from "@rivets/core";
import { recordDiagnostic } from "./diagnostics";

/**
 * Reading what the relay sends, one event at a time (web #3).
 *
 * `chat.events` used to be parsed as a whole: one event that failed the schema
 * — a newer server's event type, a field over a cap — dropped the frame
 * silently. A replay holding it was dropped again on every resubscribe, so the
 * cursor never moved and the chat sat on whatever it showed last. Now the
 * envelope (chat, coverage) is checked on its own and each event separately:
 * a bad event becomes a placeholder row (or, when it has no turn to stand in,
 * is left out), and the coverage always applies, so the cursor moves past it.
 * REST history pages go through the same per-event check, so both paths treat
 * an event alike.
 */

const ChatEventsEnvelope = z.object({
  type: z.literal("chat.events"),
  chatId: z.string(),
  events: z.array(z.unknown()),
  coverage: ChatEventsCoverage.optional(),
  hasGapBefore: z.boolean().optional(),
});

const OrgUpdateEnvelope = z.object({
  type: z.literal("org.update"),
  revision: z.number().int().positive(),
});

export type ChatEventsFrame = Extract<ServerToClient, { type: "chat.events" }>;

export type DecodedFrame =
  | { kind: "frame"; frame: ServerToClient }
  /** An `org.update` whose revision is readable but whose update is not: the
   * revision still counts, and the caller reconciles as for a gap. */
  | { kind: "unreadable-org-update"; revision: number }
  | { kind: "dropped" };

/** Same words as the server's stand-in for an event it could not keep. */
export const PLACEHOLDER_MESSAGE = "This entry could not be displayed.";

/**
 * The events of one frame or page that this build can read. An unreadable
 * event with `seq`, `ts` and its turn becomes an agent `log` placeholder at
 * the same seq; one without them is left out (live frames then show a seq gap,
 * which resubscribes; replays and pages carry coverage over it).
 */
export function readChatEvents(chatId: string, raw: readonly unknown[]): { events: ChatEvent[]; replaced: number; dropped: number } {
  const events: ChatEvent[] = [];
  let replaced = 0;
  let dropped = 0;
  for (const item of raw) {
    const parsed = ChatEvent.safeParse(item);
    if (parsed.success) {
      events.push(parsed.data);
      continue;
    }
    const placeholder = placeholderFor(chatId, item);
    if (placeholder) {
      events.push(placeholder);
      replaced += 1;
    } else {
      dropped += 1;
    }
    recordDiagnostic("event", `chat ${chatId}: unreadable event ${describeEvent(item)} — ${parsed.error.issues.slice(0, 2).map((issue) => `${issue.path.join(".") || "event"} ${issue.code}`).join("; ")}`);
  }
  if (replaced + dropped > 0) console.error("[chat-events] unreadable events", { chatId, replaced, dropped });
  return { events, replaced, dropped };
}

function placeholderFor(chatId: string, item: unknown): ChatEvent | null {
  if (!item || typeof item !== "object") return null;
  const event = item as Record<string, unknown>;
  const seq = event.seq;
  if (typeof seq !== "number" || !Number.isFinite(seq)) return null;
  const parsed = ChatEvent.safeParse({
    seq,
    chatId,
    ts: typeof event.ts === "number" ? event.ts : 0,
    userId: typeof event.userId === "string" ? event.userId : undefined,
    turnId: typeof event.turnId === "string" ? event.turnId : undefined,
    jobId: typeof event.jobId === "string" ? event.jobId : undefined,
    eventId: typeof event.eventId === "string" && event.eventId.length <= 300 ? event.eventId : undefined,
    payload: { kind: "agent", event: { type: "log", level: "warn", message: PLACEHOLDER_MESSAGE } },
  });
  return parsed.success ? parsed.data : null;
}

function describeEvent(item: unknown): string {
  if (!item || typeof item !== "object") return typeof item;
  const event = item as { seq?: unknown; payload?: { kind?: unknown; event?: { type?: unknown } } };
  const kind = event.payload?.kind === "agent" ? `agent/${String(event.payload.event?.type)}` : String(event.payload?.kind);
  return `#${String(event.seq)} (${kind})`;
}

/** One relay text frame, read as leniently as is safe. */
export function decodeServerFrame(raw: string): DecodedFrame {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    recordDiagnostic("frame", "a frame that is not JSON");
    return { kind: "dropped" };
  }
  const type = (value as { type?: unknown } | null)?.type;
  if (type === "chat.events") {
    const envelope = ChatEventsEnvelope.safeParse(value);
    if (!envelope.success) return drop(type, envelope.error);
    const { events } = readChatEvents(envelope.data.chatId, envelope.data.events);
    const frame: ChatEventsFrame = {
      type: "chat.events",
      chatId: envelope.data.chatId,
      events,
      ...(envelope.data.coverage ? { coverage: envelope.data.coverage } : {}),
      ...(envelope.data.hasGapBefore !== undefined ? { hasGapBefore: envelope.data.hasGapBefore } : {}),
    };
    return { kind: "frame", frame };
  }
  const parsed = ServerToClient.safeParse(value);
  if (parsed.success) return { kind: "frame", frame: parsed.data };
  if (type === "org.update") {
    const envelope = OrgUpdateEnvelope.safeParse(value);
    if (envelope.success) {
      drop(type, parsed.error);
      return { kind: "unreadable-org-update", revision: envelope.data.revision };
    }
  }
  return drop(type, parsed.error);
}

function drop(type: unknown, error: z.ZodError): DecodedFrame {
  const reason = error.issues.slice(0, 2).map((issue) => `${issue.path.join(".") || "frame"} ${issue.code}`).join("; ");
  console.error("[org-socket] dropped an unreadable frame", type, reason);
  recordDiagnostic("frame", `dropped ${String(type)}: ${reason}`);
  return { kind: "dropped" };
}
